import { ReactNode, useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Check, ChevronDown, Clock, Gift, Loader2, RefreshCw, Swords } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { WiredMinhasMissoes, WiredMissaoResumo, WiredMissaoStatus, wiredService } from "../../services/wired";

const INTERVALO_MS = 20_000;

const STATUS_ROTULO: Record<WiredMissaoStatus, string> = {
  em_andamento: "Em andamento",
  concluida: "Concluída",
  expirada: "Expirada",
  abandonada: "Abandonada",
};

const STATUS_COR: Record<WiredMissaoStatus, string> = {
  em_andamento: "text-amber-500",
  concluida: "text-green-600",
  expirada: "text-red-600",
  abandonada: "text-muted-foreground",
};

function formatarData(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString("pt-BR") : "";
}

function tempoRestante(iso: string | null): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "encerrando";
  const minutos = Math.floor(ms / 60_000);
  const dias = Math.floor(minutos / 1440);
  const horas = Math.floor((minutos % 1440) / 60);
  if (dias > 0) return `${dias}d ${horas}h`;
  if (horas > 0) return `${horas}h ${minutos % 60}min`;
  return `${minutos}min`;
}

interface CartaoProps {
  missao: WiredMissaoResumo;
  /** Linha curta exibida sob o nome quando recolhido. */
  resumo?: ReactNode;
  /** Barra fina de progresso (0-100) exibida no cartão recolhido. */
  progresso?: number;
  acoes?: ReactNode;
  children: ReactNode;
}

/** Cartão compacto: imagem, nome, resumo e ações; os detalhes aparecem ao expandir. */
function Cartao({ missao, resumo, progresso, acoes, children }: CartaoProps) {
  const [aberto, setAberto] = useState(false);

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="flex items-stretch gap-3 pr-3">
        {missao.imagem_url ? (
          <img
            src={missao.imagem_url}
            alt=""
            className="min-h-20 w-28 shrink-0 self-stretch rounded-l-lg border-r border-border object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="flex min-h-20 w-28 shrink-0 items-center justify-center self-stretch rounded-l-lg border-r border-border text-muted-foreground">
            <Swords className="h-8 w-8" />
          </div>
        )}
        <div className="min-w-0 flex-1 self-center py-2">
          <h3 className="truncate text-sm font-semibold text-foreground">{missao.nome}</h3>
          {resumo && <div className="truncate text-xs text-muted-foreground">{resumo}</div>}
          {progresso !== undefined && (
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full rounded-full transition-all ${progresso >= 100 ? "bg-green-500" : "bg-amber-400"}`}
                style={{ width: `${progresso}%` }}
              />
            </div>
          )}
        </div>
        {acoes && <div className="flex shrink-0 items-center gap-2 self-center">{acoes}</div>}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 self-center"
          onClick={() => setAberto((v) => !v)}
          aria-expanded={aberto}
          aria-label={aberto ? "Recolher detalhes" : "Expandir detalhes"}
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${aberto ? "rotate-180" : ""}`} />
        </Button>
      </div>

      {aberto && (
        <div className="space-y-3 border-t border-border px-3 py-3">
          {missao.descricao && <p className="whitespace-pre-line text-sm text-muted-foreground">{missao.descricao}</p>}
          {missao.recompensas.length > 0 && (
            <p className="flex flex-wrap items-center gap-1 text-xs text-amber-600">
              <Gift className="h-3 w-3" />
              Recompensa: {missao.recompensas.map((r) => `+${r.valor} ${r.rotulo}`).join(", ")}
            </p>
          )}
          {children}
        </div>
      )}
    </Card>
  );
}

export function Missoes({ onMudou }: { onMudou: () => void }) {
  const [dados, setDados] = useState<WiredMinhasMissoes | null>(null);
  const [erro, setErro] = useState("");
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [atualizando, setAtualizando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setDados(await wiredService.obterMinhasMissoes());
      setErro("");
    } catch (error: any) {
      setErro(error.response?.data?.message || "Não foi possível carregar as missões.");
    }
  }, []);

  const recarregar = async () => {
    setAtualizando(true);
    try {
      await carregar();
    } finally {
      setAtualizando(false);
    }
  };

  useEffect(() => {
    carregar();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") carregar();
    }, INTERVALO_MS);
    return () => window.clearInterval(timer);
  }, [carregar]);

  const executar = async (chave: string, acao: () => Promise<string | void>) => {
    setOcupado(chave);
    try {
      const mensagem = await acao();
      if (mensagem) enqueueSnackbar(mensagem, { variant: "success" });
      await carregar();
      onMudou();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Não foi possível concluir a ação.", { variant: "error" });
      await carregar();
    } finally {
      setOcupado(null);
    }
  };

  if (!dados) {
    return erro ? (
      <p className="text-sm text-red-600">{erro}</p>
    ) : (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={recarregar}
          disabled={atualizando}
          title="Atualizar missões"
          aria-label="Atualizar missões"
        >
          <RefreshCw className={`h-4 w-4 ${atualizando ? "animate-spin" : ""}`} />
        </Button>
      </div>
      {erro && <p className="text-sm text-red-600">{erro}</p>}

      <section className="space-y-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase text-muted-foreground">
          <Swords className="h-4 w-4" />
          Em andamento
          {dados.limite_simultaneas !== null && (
            <span className="font-normal normal-case">
              ({dados.em_andamento_total}/{dados.limite_simultaneas})
            </span>
          )}
        </h2>
        {dados.em_andamento.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma missão em andamento.</p>
        ) : (
          dados.em_andamento.map((m) => {
            const restante = tempoRestante(m.prazo_em);
            const feitos = m.objetivos.filter((o) => o.concluido).length;
            const pct = m.objetivos.length ? Math.floor((feitos / m.objetivos.length) * 100) : 0;
            return (
              <Cartao
                key={m.id}
                missao={m}
                progresso={pct}
                resumo={
                  <span className="flex items-center gap-2">
                    <span>
                      {feitos}/{m.objetivos.length} objetivos
                    </span>
                    {restante && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {restante}
                      </span>
                    )}
                  </span>
                }
                acoes={
                  <Button
                    size="sm"
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                    disabled={!m.pode_concluir || ocupado !== null}
                    onClick={() =>
                      executar(`c-${m.id}`, async () => {
                        const r = await wiredService.concluirMissao(m.id);
                        return r.ganhos.length > 0
                          ? `Missão concluída! ${r.ganhos.map((g) => `+${g.quantidade} ${g.rotulo}`).join(", ")}`
                          : "Missão concluída!";
                      })
                    }
                  >
                    {ocupado === `c-${m.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : "Concluir"}
                  </Button>
                }
              >
                <ul className="space-y-2">
                  {m.objetivos.map((o, i) => {
                    const p = Math.min(100, Math.floor((Number(o.atual) / Number(o.meta)) * 100)) || 0;
                    return (
                      <li key={i} className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-1 text-foreground">
                            {o.concluido && <Check className="h-3 w-3 text-green-600" />}
                            {o.rotulo}
                          </span>
                          <span className="font-mono text-muted-foreground">
                            {Number(o.atual).toLocaleString("pt-BR")} / {Number(o.meta).toLocaleString("pt-BR")}
                          </span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-muted">
                          <div
                            className={`h-full rounded-full transition-all ${o.concluido ? "bg-green-500" : "bg-amber-400"}`}
                            style={{ width: `${p}%` }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <p className="text-xs text-muted-foreground">Aceita em {formatarData(m.aceita_em)}</p>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={ocupado !== null}
                  onClick={() => {
                    if (!window.confirm(`Abandonar a missão "${m.nome}"? O progresso será perdido.`)) return;
                    executar(`a-${m.id}`, async () => {
                      await wiredService.abandonarMissao(m.id);
                      return "Missão abandonada.";
                    });
                  }}
                >
                  Abandonar
                </Button>
              </Cartao>
            );
          })
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold uppercase text-muted-foreground">Disponíveis</h2>
        {dados.disponiveis.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma missão disponível no momento.</p>
        ) : (
          dados.disponiveis.map((m) => (
            <Cartao
              key={m.id}
              missao={m}
              resumo={
                m.motivo ? (
                  <span className="text-amber-600">{m.motivo}</span>
                ) : (
                  `${m.qtd_sorteio !== null ? m.qtd_sorteio + m.total_obrigatorios : m.total_objetivos} objetivo(s)${m.repetivel ? " · repetível" : ""}`
                )
              }
              acoes={
                <Button
                  size="sm"
                  className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                  disabled={!m.pode_aceitar || ocupado !== null}
                  onClick={() =>
                    executar(`s-${m.id}`, async () => {
                      await wiredService.aceitarMissao(m.id);
                      return "Missão aceita!";
                    })
                  }
                >
                  {ocupado === `s-${m.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aceitar"}
                </Button>
              }
            >
              <ul className="space-y-0.5 text-xs text-muted-foreground">
                {m.qtd_sorteio ? (
                  <li>
                    🎲 {m.qtd_sorteio} objetivos sorteados entre {m.total_objetivos - m.total_obrigatorios}
                    {m.total_obrigatorios > 0 ? ` (além de ${m.total_obrigatorios} obrigatório(s))` : ""}
                  </li>
                ) : null}
                {m.objetivos.map((o, i) => (
                  <li key={i}>
                    🎯 {o.rotulo}: {Number(o.meta).toLocaleString("pt-BR")}
                    {o.obrigatorio && m.qtd_sorteio ? " (obrigatório)" : ""}
                    {o.modo === "retroativo" ? " (conta o que você já fez)" : ""}
                  </li>
                ))}
                {m.prazo_tipo === "relativo" && m.prazo_horas && <li>⏱ Prazo de {m.prazo_horas}h após aceitar</li>}
                {m.prazo_tipo === "fixo" && m.prazo_ate && <li>⏱ Até {formatarData(m.prazo_ate)}</li>}
                {m.repetivel && <li>🔁 Repetível{m.cooldown_horas ? ` a cada ${m.cooldown_horas}h` : ""}</li>}
                {m.requisitos.map((r, i) => (
                  <li key={i} className={r.atendido ? "" : "text-red-600"}>
                    {r.atendido ? "✔" : "✖"} {r.rotulo} {r.operador} {r.valor}
                  </li>
                ))}
              </ul>
              {m.motivo && m.disponivel_em && (
                <p className="text-xs text-amber-600">Disponível em {formatarData(m.disponivel_em)}.</p>
              )}
            </Cartao>
          ))
        )}
      </section>

      {dados.historico.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">Histórico</h2>
          <ul className="divide-y divide-border rounded-md border border-border">
            {dados.historico.map((h) => (
              <li key={h.id} className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                <span className="truncate text-foreground">{h.nome}</span>
                <span className="shrink-0 text-right">
                  <span className={`font-semibold ${STATUS_COR[h.status]}`}>{STATUS_ROTULO[h.status]}</span>
                  <span className="text-muted-foreground"> · {formatarData(h.finalizada_em ?? h.aceita_em)}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
